import { useEffect, useState } from 'react'
import baseUrl from '../../api/baseUrl'
import {
  type LabOrderDetail,
  type OrderStatus,
  type PaymentStatus,
} from './labOrderTypes'

type LocalOrderTrackingPanelProps = {
  order: LabOrderDetail
  onUpdated: (order: LabOrderDetail) => void
}

const ORDER_STATUSES: OrderStatus[] = [
  'Pending',
  'Accepted',
  'In Progress',
  'Completed',
  'Cancelled',
]

export default function LocalOrderTrackingPanel({
  order,
  onUpdated,
}: LocalOrderTrackingPanelProps) {
  const [orderStatus, setOrderStatus] = useState<OrderStatus>(order.orderStatus)
  const [payment, setPayment] = useState<PaymentStatus>(order.payment)
  const [amount, setAmount] = useState('')
  const [method, setMethod] = useState('Cash')
  const [saving, setSaving] = useState(false)
  const [paying, setPaying] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')

  useEffect(() => {
    setOrderStatus(order.orderStatus)
    setPayment(order.payment)
  }, [order._id, order.orderStatus, order.payment])

  const orderApiId = order._id

  async function saveStatus() {
    setSaving(true)
    setError('')
    setMessage('')
    try {
      const res = await baseUrl.patch(
        `client/labs/orders/${encodeURIComponent(orderApiId)}`,
        { orderStatus, payment },
      )
      onUpdated(res.data.data)
      setMessage('Order updated')
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || 'Failed to update order'
      setError(msg)
    } finally {
      setSaving(false)
    }
  }

  async function recordPayment(e: React.FormEvent) {
    e.preventDefault()
    const value = Number(amount)
    if (!value || value <= 0) {
      setError('Enter a valid payment amount')
      return
    }
    setPaying(true)
    setError('')
    setMessage('')
    try {
      const res = await baseUrl.post(
        `client/labs/orders/${encodeURIComponent(orderApiId)}/payments`,
        { amount: value, method, total: value },
      )
      onUpdated(res.data.data)
      setOrderStatus(res.data.data.orderStatus)
      setPayment(res.data.data.payment)
      setAmount('')
      setMessage('Payment recorded')
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || 'Failed to record payment'
      setError(msg)
    } finally {
      setPaying(false)
    }
  }

  return (
    <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 dark:border-primary/30 dark:bg-primary/10">
      <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
        Self-tracking
      </h3>
      <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
        This order is tracked by your clinic only. Update status and payments
        here.
      </p>

      {error ? (
        <p className="mt-3 text-sm text-red-600">{error}</p>
      ) : null}
      {message ? (
        <p className="mt-3 text-sm text-emerald-700 dark:text-emerald-300">
          {message}
        </p>
      ) : null}

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <label className="block text-sm">
          <span className="mb-1 block font-medium text-slate-600 dark:text-slate-400">
            Order status
          </span>
          <select
            value={orderStatus}
            onChange={(e) => setOrderStatus(e.target.value as OrderStatus)}
            className="w-full rounded-lg border dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm"
          >
            {ORDER_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-sm">
          <span className="mb-1 block font-medium text-slate-600 dark:text-slate-400">
            Payment status
          </span>
          <select
            value={payment}
            onChange={(e) => setPayment(e.target.value as PaymentStatus)}
            className="w-full rounded-lg border dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm"
          >
            <option value="Unpaid">Unpaid</option>
            <option value="Partial">Partial</option>
            <option value="Settled">Settled</option>
          </select>
        </label>
      </div>

      <button
        type="button"
        onClick={saveStatus}
        disabled={saving}
        className="mt-3 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
      >
        {saving ? 'Saving…' : 'Save status'}
      </button>

      <form onSubmit={recordPayment} className="mt-5 border-t border-primary/15 pt-4">
        <p className="text-sm font-medium text-slate-800 dark:text-slate-200">
          Record payment
        </p>
        <div className="mt-3 grid gap-3 sm:grid-cols-[1fr_8rem_auto] sm:items-end">
          <label className="block text-sm">
            <span className="mb-1 block text-slate-600 dark:text-slate-400">
              Amount (LKR)
            </span>
            <input
              type="number"
              min={0}
              step={0.01}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full rounded-lg border dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm"
            />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block text-slate-600 dark:text-slate-400">
              Method
            </span>
            <select
              value={method}
              onChange={(e) => setMethod(e.target.value)}
              className="w-full rounded-lg border dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm"
            >
              <option value="Cash">Cash</option>
              <option value="Card">Card</option>
              <option value="Bank transfer">Bank transfer</option>
            </select>
          </label>
          <button
            type="submit"
            disabled={paying}
            className="rounded-lg border border-primary px-4 py-2 text-sm font-medium text-primary hover:bg-primary/5 disabled:opacity-50"
          >
            {paying ? 'Adding…' : 'Add payment'}
          </button>
        </div>
      </form>
    </div>
  )
}
